import type { BoxProps } from "@mui/material/Box";
import type { CardProps } from "@mui/material/Card";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CardHeader from "@mui/material/CardHeader";
import ListItemText from "@mui/material/ListItemText";

import { Iconify } from "../components/iconify";
import { Scrollbar } from "../components/scrollbar";
import { Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IStoreState, useAppDispatch, useAppSelector } from "../../../redux";
import React from "react";
import { fetchAllUsersProfileAsync } from "../../../redux/settings/user-profile/user-profile.actions";
import {
  clearAllUsersStateSync,
  IUserProfile,
} from "../../../redux/settings/user-profile";
import { ALL_USERS_LIST_ROUTE } from "../../Settings/UserProfile/UserProfile.constant";
import { useNavigate } from "react-router-dom";
import {
  clearOrdersStateSync,
  fetchOrdersListAsync,
  fetchOrdersListWithArgsAsync,
  IOrder,
} from "../../../redux/orders";
import {
  POS_APP_MENU_ROUTE_NEW_ORDER,
  POS_ORDERS_ROUTE,
} from "../../MenuAndOrders/MenuAndOrders.constants";
import { fetchDashboardTakeAwayOrdersAsync } from "../../../redux/analytics/analytics.actions";
import { useOutletContext } from "../../../providers";
import { StandardCard } from "../../../Components/Card";
// ----------------------------------------------------------------------

type Props = CardProps & {
  title?: string;
  subheader?: string;
  list: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string;
  }[];
};

export function QuickOrdersVIew() {
  const {
    outlet_details: { outlet_uuid },
  } = useOutletContext();
  const {
    data: allOrders,
    loading,
    totalRecords,
  } = useAppSelector(
    (storeState: IStoreState) => storeState.analytics.take_away_orders_list
  );
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (outlet_uuid) dispatch(fetchDashboardTakeAwayOrdersAsync(outlet_uuid));

    return () => {
      dispatch(clearOrdersStateSync());
    };
  }, [outlet_uuid]);

  const handleOnViewAllUsersClick = () => {
    navigate(`${POS_APP_MENU_ROUTE_NEW_ORDER}?type=TAKE_AWAY`);
  };

  return (
    <StandardCard
      heading={"Take Away Orders"}
      rightHeading={
        <Button
          size="small"
          color="inherit"
          sx={{
            backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--primary-color)",
            color: isLightMode ? "var(--text-light-color)" : "var(--text-dark-color)",
            border: isLightMode ? "1px solid var(--border-light-color)" : "1px solid var(--border-dark-color)",
            "&:hover": {
              backgroundColor: isLightMode ? "var(--secondary-color)" : "var(--tertiary-color)",
              opacity: isLightMode ? 0.85 : 1,
            }
          }}
          onClick={handleOnViewAllUsersClick}
          endIcon={
            <Iconify
              icon="eva:arrow-ios-forward-fill"
              width={18}
              sx={{ ml: -0.5 }}
            />
          }
        >
          Order Now
        </Button>
      }
    >
      <Box
        sx={{
          gap: 2,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5">Order Number</Typography>
        <Typography variant="h5">Sub Total</Typography>
        <Typography variant="h5">Total</Typography>
      </Box>

      <Scrollbar sx={{ minHeight: 200 }}>
        <Box
          sx={{
            p: 3,
            gap: 3,
            display: "flex",
            flexDirection: "column",
            minWidth: 200,
          }}
        >
          {allOrders.slice(0, 5).map((item) => (
            <Item key={item.order_uuid} item={item} />
          ))}
        </Box>
      </Scrollbar>
    </StandardCard>
  );
}

// ----------------------------------------------------------------------

type ItemProps = BoxProps & {
  item: IOrder;
};

function Item({ item, sx, ...other }: ItemProps) {
  return (
    <Box
      key={item.order_uuid}
      sx={{
        gap: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        ...sx,
      }}
      {...other}
    >
      <Typography variant="h5">{item.order_number}</Typography>
      <Typography variant="h5" sx={{ opacity: 0.7, textAlign: "center" }}>
        {item.sub_total}
      </Typography>
      <Typography variant="h5" sx={{ opacity: 0.7 }}>
        {item.total_amount}
      </Typography>
    </Box>
  );
}
